import { Fragment } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { DetailNotificationCrumbs } from 'practice/constants/notification-management.crumbs';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { __PDND_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { PDNDNotificationDetail } from 'shell/models/pdnd-notification';
import { DateTime } from 'luxon';
import NotificationManagementDetailForm, {
  NotificationManagementFormVM,
} from 'practice/components/notification-management/detail/form';
import { parseProgressivoPratice } from 'practice/utils/practice-research-transform';

const NotificationManagementDetail = ({
  goBackUrl = '',
  breadcrumbs,
}: {
  goBackUrl?: string;
  breadcrumbs?: BreadcrumbItem[];
}): React.ReactElement => {
  const params = useParams();
  const { id } = params;

  const navigate = useNavigate();

  const [ultimoScaricoData] = useHttpFetch<HttpResponse<string>, DateTime | null>(
    `${__PDND_API_URL__}/notifica/timestamp-ultimo-scarico`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: [],
      },
      transformer: (response) => {
        return response.data.payload ? DateTime.fromISO(response.data.payload) : null;
      },
    }
  );

  const [notifications] = useHttpFetch<
    HttpResponse<PDNDNotificationDetail>,
    NotificationManagementFormVM
  >(`${__PDND_API_URL__}/notifica/dettaglio/${id}`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: [],
    },
    transformer: (response) => {
      const errors = response.data.payload.errori.map((el) => ({
        ...el,
        isEdit: true,
      }));
      const events = response.data.payload.eventi;
      const progressivoPratica = parseProgressivoPratice(response.data.payload.progressivoPratica);

      const [recipient] = response.data.payload.contenutoNotifica.recipients;
      const { physicalAddress, ...rest } = recipient || {};
      return {
        item: response.data.payload,
        state: response.data.payload.stato,
        practiceId: `${response.data.payload.tipoRegistro}.${response.data.payload.idAnnoPratica}.${progressivoPratica}`,
        communicationId: response.data.payload.iun?.toString() || '',
        communicationType: response.data.payload.codiceAmbito,
        transmissionType: response.data.payload.tipoTrasmissione,
        trasmissionDate: response.data.payload.timestampInserimento
          ? DateTime.fromISO(response.data.payload.timestampInserimento)
          : null,
        stateDate: response.data.payload.timestampUltimoAggiornamento
          ? DateTime.fromISO(response.data.payload.timestampUltimoAggiornamento)
          : null,
        recipient: {
          ...physicalAddress,
          ...rest,
          digitalDomicile: rest.digitalDomicile?.address,
          typedigitalDomicile: rest.digitalDomicile?.type,
        },
        stateItems: (
          [...events, ...errors] as (PDNDNotificationDetail['eventi'][0] & { isEdit?: boolean })[]
        ).map((el) => ({
          id: el.idEventoNotifica?.toString(),
          communicationType: el.channel,
          date: el.timestampEventoSmn
            ? DateTime.fromISO(el.timestampEventoSmn).toFormat('dd/MM/yyyy')
            : '',
          state: el.stato,
          transmissionType: el.channel,
          hasEdit: el.isEdit,
          item: el,
        })),
      };
    },
  });

  const castedData = notifications.data as NotificationManagementFormVM;

  const ultimoScarico = ultimoScaricoData.data as DateTime;

  if (notifications.isLoading || notifications.pristine) {
    return <></>;
  }

  return (
    <Fragment>
      <Breadcrumbs items={breadcrumbs || DetailNotificationCrumbs} />
      <NotificationManagementDetailForm
        stateItems={castedData.stateItems || []}
        formData={castedData}
        ultimoScarico={ultimoScarico}
        id={id}
        onExit={() => {
          navigate(generatePath(goBackUrl || '/pratiche/notifiche', { ...params }));
        }}
      />
    </Fragment>
  );
};

NotificationManagementDetail.displayName = 'Notification Management Detail';

export default NotificationManagementDetail;
